import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../api/client.ts";
import { isApiRequestError } from "../api/errors.ts";
import { keys } from "../api/hooks.ts";

/**
 * The decision-mail switch (`/api/me/notifications`, default on). Optimistic: the checkbox flips at
 * once; a failed write rolls it back and `saveFailed` says so. Divers only: pass `enabled=false`
 * for staff and nothing is requested.
 */
export function useDecisionMails(enabled: boolean) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: keys.meNotifications,
    queryFn: ({ signal }) => api.meNotifications(signal),
    enabled,
    staleTime: 30_000,
    retry: false,
  });
  // The value shown while a write runs lives in component state: react-query notifies its observers
  // a tick later, and for that tick a controlled checkbox would snap back to the old value.
  const [pending, setPending] = useState<boolean | null>(null);
  const write = useMutation({
    mutationFn: (decisionMails: boolean) => api.updateMeNotifications({ decisionMails }),
    onMutate: () => {
      // revert:false, or the cancel puts the pre-fetch (old) value back
      void qc.cancelQueries({ queryKey: keys.meNotifications }, { revert: false });
    },
    onSuccess: (saved) => qc.setQueryData(keys.meNotifications, saved),
    // success shows the saved value, failure the old one (the rollback)
    onSettled: () => setPending(null),
  });
  return {
    /** undefined until the first answer */
    on: pending ?? query.data?.decisionMails,
    loadFailed: query.isError,
    saveFailed: write.isError,
    /** 429: the per-user write limiter (30/min) */
    rateLimited: isApiRequestError(write.error) && write.error.code === "rate_limited",
    saving: write.isPending,
    /** the last write was accepted (announced to screen readers) */
    saved: write.isSuccess,
    set: (v: boolean) => {
      setPending(v);
      write.mutate(v);
    },
    retry: () => void query.refetch(),
  };
}
